import React, { useEffect, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { Eye, Globe, MapPin, ChevronLeft, ChevronRight, Download, RefreshCw, Users, BarChart3, TrendingUp, ExternalLink } from "lucide-react";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinningIcon = styled(RefreshCw)`
  animation: ${spin} 1s linear infinite;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: var(--bg-card);
  padding: 20px;
  border-radius: 16px;
  border: 1px solid var(--glass-border);
  display: flex;
  align-items: center;
  gap: 16px;
  .icon {
    width: 50px;
    height: 50px;
    border-radius: 12px;
    background: rgba(99, 102, 241, 0.1);
    color: var(--primary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .info h3 { font-size: 1.5rem; margin-bottom: 2px; }
  .info p { color: var(--text-muted); font-size: 0.85rem; }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid var(--glass-border);
  background: var(--bg-card);
  color: var(--text-main);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  font-size: 0.9rem;
  &:hover { background: rgba(99, 102, 241, 0.1); border-color: var(--primary); color: var(--primary); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Table = styled.div`
  background: var(--bg-card);
  border-radius: 16px;
  border: 1px solid var(--glass-border);
  overflow-x: auto;
  margin-bottom: 20px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 60px 1.1fr 0.8fr 1.5fr 1.5fr 2fr 1fr;
  padding: 14px 16px;
  background: rgba(99, 102, 241, 0.05);
  border-bottom: 1px solid var(--glass-border);
  font-weight: 600;
  font-size: 0.8rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 900px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1.1fr 0.8fr 1.5fr 1.5fr 2fr 1fr;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  align-items: center;
  font-size: 0.85rem;
  transition: background 0.15s;
  min-width: 900px;
  &:hover { background: rgba(99, 102, 241, 0.03); }
  &:last-child { border-bottom: none; }
`;

const CountryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  background: rgba(99, 102, 241, 0.1);
  font-size: 0.85rem;
  white-space: nowrap;
`;

const PathText = styled.span`
  color: var(--primary);
  font-family: monospace;
  font-size: 0.8rem;
  word-break: break-all;
`;

const AddressText = styled.span`
  font-size: 0.8rem;
  color: var(--text-main);
  display: flex;
  align-items: flex-start;
  gap: 4px;
  line-height: 1.3;
`;

const MapLink = styled.a`
  color: var(--primary);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.8rem;
  white-space: nowrap;
  &:hover { text-decoration: underline; }
`;

const DateText = styled.span`
  color: var(--text-muted);
  font-size: 0.8rem;
  white-space: nowrap;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
`;

const PageBtn = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid var(--glass-border);
  background: ${props => props.$active ? "var(--primary)" : "var(--bg-card)"};
  color: ${props => props.$active ? "#fff" : "var(--text-main)"};
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  font-size: 0.9rem;
  &:hover:not(:disabled) { border-color: var(--primary); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PageInfo = styled.span`
  color: var(--text-muted);
  font-size: 0.9rem;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: var(--bg-card);
  border-radius: 16px;
  border: 1px solid var(--glass-border);
  padding: 24px;
`;

const CardTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BarChart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BarLabel = styled.span`
  min-width: 100px;
  font-size: 0.85rem;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 24px;
  background: rgba(99, 102, 241, 0.05);
  border-radius: 6px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  background: linear-gradient(to right, var(--primary), var(--secondary));
  border-radius: 6px;
  min-width: 2px;
  transition: width 0.4s ease;
`;

const BarValue = styled.span`
  min-width: 40px;
  text-align: right;
  font-size: 0.85rem;
  font-weight: 600;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: var(--text-muted);
  gap: 12px;
`;

const FlagEmoji = ({ code }) => {
    if (!code || code === "XX") return null;
    try {
        return String.fromCodePoint(
            ...code.toUpperCase().split("").map(c => 0x1F1E6 - 65 + c.charCodeAt(0))
        );
    } catch (e) {
        return null;
    }
};

const ManageVisitors = () => {
    const [visitors, setVisitors] = useState([]);
    const [stats, setStats] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchVisitors = useCallback(async (page) => {
        const token = localStorage.getItem("adminToken");
        const apiUrl = process.env.REACT_APP_API_URL || "";
        try {
            setLoading(true);
            const res = await fetch(apiUrl + "/api/admin/visitors?page=" + page + "&limit=50", {
                headers: { Authorization: "Bearer " + token }
            });
            if (res.ok) {
                const data = await res.json();
                setVisitors(data.visitors);
                setPagination(data.pagination);
            }
        } catch (err) {
            console.error("Failed to fetch visitors:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchStats = useCallback(async () => {
        const token = localStorage.getItem("adminToken");
        const apiUrl = process.env.REACT_APP_API_URL || "";
        try {
            const res = await fetch(apiUrl + "/api/admin/visitors/stats", {
                headers: { Authorization: "Bearer " + token }
            });
            if (res.ok) {
                setStats(await res.json());
            }
        } catch (err) {
            console.error("Failed to fetch stats:", err);
        }
    }, []);

    useEffect(() => {
        fetchVisitors(1);
        fetchStats();
    }, [fetchVisitors, fetchStats]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchVisitors(pagination.page);
        await fetchStats();
        setRefreshing(false);
    };

    const handleExport = () => {
        const token = localStorage.getItem("adminToken");
        const apiUrl = process.env.REACT_APP_API_URL || "";
        window.open(apiUrl + "/api/admin/visitors/export?token=" + token, "_blank");
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    };

    const maxDaily = stats ? Math.max(...stats.dailyVisits.map(d => d.count), 1) : 1;
    const maxCountry = stats ? Math.max(...stats.topCountries.map(c => c.count), 1) : 1;
    const maxPage = stats ? Math.max(...stats.topPages.map(p => p.count), 1) : 1;

    return (
        <div>
            <Title><Eye size={28} color="var(--primary)" /> Visitor Analytics</Title>

            {stats && (
                <StatsGrid>
                    <StatCard>
                        <div className="icon"><Eye size={24} /></div>
                        <div className="info"><h3>{stats.today.toLocaleString()}</h3><p>Visits Today</p></div>
                    </StatCard>
                    <StatCard>
                        <div className="icon"><Users size={24} /></div>
                        <div className="info"><h3>{stats.uniqueToday.toLocaleString()}</h3><p>Unique IPs Today</p></div>
                    </StatCard>
                    <StatCard>
                        <div className="icon"><TrendingUp size={24} /></div>
                        <div className="info"><h3>{stats.thisWeek.toLocaleString()}</h3><p>This Week</p></div>
                    </StatCard>
                    <StatCard>
                        <div className="icon"><BarChart3 size={24} /></div>
                        <div className="info"><h3>{stats.thisMonth.toLocaleString()}</h3><p>This Month</p></div>
                    </StatCard>
                </StatsGrid>
            )}

            {stats && stats.topCountries.length > 0 && (
                <TwoCol>
                    <Card>
                        <CardTitle><Globe size={18} /> Top Countries</CardTitle>
                        <BarChart>
                            {stats.topCountries.map((c, i) => (
                                <BarRow key={i}>
                                    <BarLabel><FlagEmoji code={c.country_code} /> {c.country}</BarLabel>
                                    <BarTrack><BarFill style={{ width: ((c.count / maxCountry) * 100) + "%" }} /></BarTrack>
                                    <BarValue>{c.count}</BarValue>
                                </BarRow>
                            ))}
                        </BarChart>
                    </Card>
                    <Card>
                        <CardTitle><BarChart3 size={18} /> Top Pages</CardTitle>
                        <BarChart>
                            {stats.topPages.slice(0, 8).map((p, i) => (
                                <BarRow key={i}>
                                    <BarLabel title={p.path} style={{ minWidth: 140, fontFamily: "monospace", fontSize: "0.8rem" }}>{p.path}</BarLabel>
                                    <BarTrack><BarFill style={{ width: ((p.count / maxPage) * 100) + "%" }} /></BarTrack>
                                    <BarValue>{p.count}</BarValue>
                                </BarRow>
                            ))}
                        </BarChart>
                    </Card>
                </TwoCol>
            )}

            {stats && stats.dailyVisits.length > 0 && (
                <Card style={{ marginBottom: 30 }}>
                    <CardTitle><TrendingUp size={18} /> Daily Visits (Last 7 Days)</CardTitle>
                    <BarChart>
                        {stats.dailyVisits.map((d, i) => (
                            <BarRow key={i}>
                                <BarLabel>{new Date(d.date).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" })}</BarLabel>
                                <BarTrack><BarFill style={{ width: ((d.count / maxDaily) * 100) + "%" }} /></BarTrack>
                                <BarValue>{d.count}</BarValue>
                            </BarRow>
                        ))}
                    </BarChart>
                </Card>
            )}

            <Toolbar>
                <div style={{ display: "flex", gap: 10 }}>
                    <Btn onClick={handleRefresh} disabled={refreshing}>
                        {refreshing ? <SpinningIcon size={16} /> : <RefreshCw size={16} />}
                        {refreshing ? "Updating..." : "Refresh"}
                    </Btn>
                    <Btn onClick={handleExport}><Download size={16} /> Export CSV</Btn>
                </div>
                <PageInfo>Total: {pagination.total.toLocaleString()} visitors</PageInfo>
            </Toolbar>

            <Table>
                <TableHeader>
                    <div>#</div>
                    <div>IP Address</div>
                    <div>Country</div>
                    <div>Location</div>
                    <div>Address</div>
                    <div>Page</div>
                    <div>Date</div>
                </TableHeader>
                {loading ? (
                    <LoadingContainer><SpinningIcon size={20} /> Loading...</LoadingContainer>
                ) : visitors.length === 0 ? (
                    <LoadingContainer>No visitors yet</LoadingContainer>
                ) : (
                    visitors.map((v, i) => {
                        const mapUrl = v.latitude && v.longitude
                            ? "https://www.google.com/maps?q=" + v.latitude + "," + v.longitude
                            : null;
                        return (
                            <TableRow key={v.id}>
                                <div style={{ color: "var(--text-muted)" }}>{(pagination.page - 1) * pagination.limit + i + 1}</div>
                                <div style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{v.ip_address}</div>
                                <div>
                                    <CountryBadge>
                                        <FlagEmoji code={v.country_code} /> {v.country || "Unknown"}
                                    </CountryBadge>
                                </div>
                                <div style={{ fontSize: "0.85rem" }}>
                                    <MapPin size={14} style={{ marginRight: 4, opacity: 0.5 }} />
                                    {v.city || "-"}, {v.region || ""}
                                </div>
                                <div>
                                    <AddressText title={v.address || ""}>
                                        {v.address ? v.address.substring(0, 60) + (v.address.length > 60 ? "..." : "") : "-"}
                                    </AddressText>
                                    {mapUrl && (
                                        <MapLink href={mapUrl} target="_blank" rel="noopener noreferrer">
                                            <ExternalLink size={12} /> Map
                                        </MapLink>
                                    )}
                                </div>
                                <div><PathText title={v.path}>{v.path}</PathText></div>
                                <div><DateText>{formatDate(v.created_at)}</DateText></div>
                            </TableRow>
                        );
                    })
                )}
            </Table>

            {pagination.totalPages > 1 && (
                <Pagination>
                    <PageBtn disabled={pagination.page <= 1} onClick={() => fetchVisitors(pagination.page - 1)}>
                        <ChevronLeft size={16} /> Prev
                    </PageBtn>
                    <PageInfo>Page {pagination.page} of {pagination.totalPages}</PageInfo>
                    <PageBtn disabled={pagination.page >= pagination.totalPages} onClick={() => fetchVisitors(pagination.page + 1)}>
                        Next <ChevronRight size={16} />
                    </PageBtn>
                </Pagination>
            )}
        </div>
    );
};

export default ManageVisitors;
